import { z } from 'zod'
import { toast } from 'sonner'
import { useState } from 'react'
import { X } from 'lucide-react'
import { paths } from '@/routes/paths'
import { useForm } from 'react-hook-form'
import { uploadFile } from '@/lib/firebase'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { fError } from '@/utils/format-error'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useBoolean } from '@/hooks/use-boolean'
import { useRouter } from '@/routes/hooks/use-router'
import { zodResolver } from '@hookform/resolvers/zod'
import axiosInstance, { endpoints } from '@/lib/axios'
import RichTextEditor from '@/components/ui/rich-text-editor'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

import type {
  Article,
  CreateArticleData,
  UpdateArticleData,
  FileUpload,
} from './types'

// Schema definition
const articleFormSchema = z.object({
  title: z.string().min(1, 'กรุณาป้อนชื่อ RFC Talk'),
  content: z.string().min(1, 'กรุณาป้อนเนื้อหา'),
  isPublished: z.boolean(),
})

type FormData = z.infer<typeof articleFormSchema>

interface ArticleFormProps {
  article?: Article // For edit mode
  mode: 'create' | 'edit'
}

export default function ArticleForm({ article, mode }: ArticleFormProps) {
  const router = useRouter()
  const submitting = useBoolean(false)
  const [headerImage, setHeaderImage] = useState<FileUpload | null>(
    article?.imageHeaderUrl
      ? {
          url: article.imageHeaderUrl,
          fileName: 'header-image',
        }
      : null,
  )
  // const [additionalImages, setAdditionalImages] = useState<FileUpload[]>(
  //   article?.imageUrls
  //     ? article.imageUrls.map((url, index) => ({
  //         url,
  //         fileName: `image-${index + 1}`,
  //       }))
  //     : [],
  // )

  const form = useForm<FormData>({
    resolver: zodResolver(articleFormSchema),
    defaultValues: {
      title: article?.title || '',
      content: article?.content || '',
      isPublished: article?.isPublished || false,
    },
  })

  const handleHeaderImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setHeaderImage({
        url: file,
        fileName: file.name,
      })
    }
  }

  // const handleAdditionalImagesChange = (
  //   e: React.ChangeEvent<HTMLInputElement>,
  // ) => {
  //   const files = Array.from(e.target.files || [])
  //   const newImages = files.map((file) => ({
  //     url: file,
  //     fileName: file.name,
  //   }))
  //   setAdditionalImages([...additionalImages, ...newImages])
  // }

  // const removeAdditionalImage = (index: number) => {
  //   setAdditionalImages(additionalImages.filter((_, i) => i !== index))
  // }

  const onSubmit = async (data: FormData) => {
    try {
      submitting.onTrue()

      // Upload header image
      let headerImageUrl = ''
      if (headerImage) {
        if (typeof headerImage.url === 'string') {
          headerImageUrl = headerImage.url
        } else {
          headerImageUrl = await uploadFile(
            headerImage.url,
            'articles/headers',
            headerImage.fileName,
          )
        }
      }

      // Upload additional images
      // const additionalImageUrls: string[] = []
      // for (const image of additionalImages) {
      //   if (typeof image.url === 'string') {
      //     additionalImageUrls.push(image.url)
      //   } else {
      //     const url = await uploadFile(
      //       image.url,
      //       'articles/additional',
      //       image.fileName,
      //     )
      //     additionalImageUrls.push(url)
      //   }
      // }

      // Prepare article data
      const articleData = {
        title: data.title,
        content: data.content,
        imageHeaderUrl: headerImageUrl,
        // imageUrls: additionalImageUrls,
        isPublished: data.isPublished,
      }

      if (mode === 'edit' && article) {
        // Update existing article
        const updateData: UpdateArticleData = articleData
        await axiosInstance.patch(
          endpoints.articles.update(article.id),
          updateData,
        )
        toast.success('แก้ไข RFC Talk สำเร็จ')
      } else {
        // Create new article
        const createData: CreateArticleData = articleData
        await axiosInstance.post(endpoints.articles.create, createData)
        toast.success('สร้าง RFC Talk สำเร็จ')
      }

      router.push(paths.articles)
    } catch (error) {
      toast.error(fError(error))
    } finally {
      submitting.onFalse()
    }
  }

  const handleBack = () => {
    router.push(paths.articles)
  }

  return (
    <div className="space-y-6">
      {/* Form */}
      <Form {...form}>
        <form
          id="article-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-6">
              <Card className="border rounded-lg">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-black">
                    ข้อมูลหลัก
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Title */}
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          ชื่อ RFC Talk
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="ป้อนชื่อ RFC Talk"
                            className="rounded-lg border"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Content */}
                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-medium text-black">
                          เนื้อหา
                        </FormLabel>
                        <FormControl>
                          <RichTextEditor
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="ป้อนเนื้อหา RFC Talk"
                            className="min-h-[200px]"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>

              {/* Images Section */}
              <Card className="border rounded-lg">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-black">
                    รูปภาพ
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Header Image */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">
                      รูปภาพ
                    </Label>
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleHeaderImageChange}
                        className="rounded-lg border"
                      />
                      {headerImage && (
                        <div className="relative inline-block">
                          <img
                            src={
                              typeof headerImage.url === 'string'
                                ? headerImage.url
                                : URL.createObjectURL(headerImage.url)
                            }
                            alt="Header"
                            className="w-32 h-32 object-cover rounded-lg border"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                            onClick={() => setHeaderImage(null)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Additional Images */}
                  {/* <div className="space-y-2">
                    <Label className="text-sm font-medium text-black">
                      รูปภาพเพิ่มเติม
                    </Label>
                    <div className="space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleAdditionalImagesChange}
                        className="rounded-lg border"
                      />
                      {additionalImages.length > 0 && (
                        <div className="grid grid-cols-4 gap-2">
                          {additionalImages.map((image, index) => (
                            <div key={index} className="relative">
                              <img
                                src={
                                  typeof image.url === 'string'
                                    ? image.url
                                    : URL.createObjectURL(image.url)
                                }
                                alt={`Additional ${index + 1}`}
                                className="w-24 h-24 object-cover rounded-lg border"
                              />
                              <Button
                                type="button"
                                variant="destructive"
                                size="sm"
                                className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0"
                                onClick={() => removeAdditionalImage(index)}
                              >
                                <X className="h-2 w-2" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div> */}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Status */}
              <Card className="border rounded-lg">
                <CardHeader>
                  <CardTitle className="text-base font-semibold text-black">
                    สถานะ
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="isPublished"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium text-black">
                            เผยแพร่
                          </FormLabel>
                          <div className="text-xs text-muted-foreground">
                            เปิดใช้งาน RFC Talk นี้
                          </div>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 py-6 border-t">
            <Button type="submit" disabled={submitting.value}>
              {mode === 'edit' ? 'แก้ไข' : 'สร้าง'}
            </Button>
            <Button type="button" variant="outline" onClick={handleBack}>
              ยกเลิก
            </Button>
          </div>
        </form>
      </Form>
    </div>
  )
}
